import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save,
  Download,
  Refresh,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import FormSection from '../components/FormSection';
import { downloadPDF } from '../utils/PDFGenerator';
import { FORM_SECTIONS, SECTION_TITLES } from '../utils/constants';

const App = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showSummary, setShowSummary] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const steps = [
    { id: FORM_SECTIONS.CLIENT_DETAILS, label: SECTION_TITLES[FORM_SECTIONS.CLIENT_DETAILS] },
    { id: FORM_SECTIONS.INCOME, label: SECTION_TITLES[FORM_SECTIONS.INCOME] },
    { id: FORM_SECTIONS.DEDUCTIONS, label: SECTION_TITLES[FORM_SECTIONS.DEDUCTIONS] },
    { id: FORM_SECTIONS.TAX_OFFSETS, label: SECTION_TITLES[FORM_SECTIONS.TAX_OFFSETS] },
    { id: FORM_SECTIONS.SPOUSE_INFO, label: SECTION_TITLES[FORM_SECTIONS.SPOUSE_INFO] }
  ];

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('taxChecklistData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData.formData || {});
        setCompletedSteps(new Set(parsedData.completedSteps || []));
        setCurrentStep(parsedData.currentStep || 0);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever form data changes
  useEffect(() => {
    const dataToSave = {
      formData,
      completedSteps: Array.from(completedSteps),
      currentStep
    };
    localStorage.setItem('taxChecklistData', JSON.stringify(dataToSave));
  }, [formData, completedSteps, currentStep]);

  const handleSectionComplete = (section, values) => {
    setFormData(prev => ({
      ...prev,
      [section]: values
    }));
    
    setCompletedSteps(prev => new Set([...prev, section]));
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last section completed
      setShowSummary(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Allow navigation to completed steps or the next available step
    if (stepIndex <= currentStep || completedSteps.has(steps[stepIndex].id)) {
      setCurrentStep(stepIndex);
    }
  };

  const handleGeneratePDF = () => {
    try {
      downloadPDF(formData);
      setSnackbar({
        open: true,
        message: 'PDF generated successfully!',
        severity: 'success'
      });
      setShowSummary(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error generating PDF. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleSaveDraft = () => {
    setSnackbar({
      open: true,
      message: 'Draft saved successfully!',
      severity: 'success'
    });
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all data? This action cannot be undone.')) {
      setFormData({});
      setCompletedSteps(new Set());
      setCurrentStep(0);
      localStorage.removeItem('taxChecklistData');
      setSnackbar({
        open: true,
        message: 'Form reset successfully!',
        severity: 'info'
      });
    }
  };

  const getStepIcon = (stepIndex) => {
    const stepId = steps[stepIndex].id;
    if (completedSteps.has(stepId)) {
      return <CheckCircle color="success" />;
    }
    if (stepIndex === currentStep) {
      return <Warning color="primary" />;
    }
    return null;
  };

  const currentSection = steps[currentStep].id;
  const isLastSection = currentStep === steps.length - 1;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            2025 Tax Return Checklist
          </Typography>
          <Tooltip title="Save Draft">
            <IconButton color="inherit" onClick={handleSaveDraft}>
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Form">
            <IconButton color="inherit" onClick={handleReset}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id} completed={completedSteps.has(step.id)}>
                <StepLabel
                  onClick={() => handleStepClick(index)}
                  sx={{ cursor: 'pointer' }}
                  StepIconComponent={() => getStepIcon(index)}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <FormSection
          section={currentSection}
          formData={formData}
          onSectionComplete={handleSectionComplete}
          onBack={currentStep > 0 ? handleBack : null}
          isLastSection={isLastSection}
        />

        {/* Progress Summary */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Progress Summary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completed: {completedSteps.size} of {steps.length} sections
          </Typography>
          <Box sx={{ mt: 1 }}>
            {steps.map((step, index) => (
              <Typography
                key={step.id}
                variant="body2"
                sx={{
                  color: completedSteps.has(step.id) ? 'success.main' : 'text.secondary',
                  fontWeight: completedSteps.has(step.id) ? 'bold' : 'normal'
                }}
              >
                {completedSteps.has(step.id) ? '✓' : '○'} {step.label}
              </Typography>
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Summary Dialog */}
      <Dialog
        open={showSummary}
        onClose={() => setShowSummary(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Your Tax Checklist</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You have completed all sections of the tax checklist. Please review the information below before generating your PDF.
          </Typography>
          
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {Object.entries(formData).map(([section, data]) => (
              <Box key={section} sx={{ mb: 2 }}>
                <Typography variant="h6" color="primary">
                  {SECTION_TITLES[section]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Object.keys(data).length} fields completed
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummary(false)}>
            Back to Edit
          </Button>
          <Button
            onClick={handleGeneratePDF}
            variant="contained"
            startIcon={<Download />}
          >
            Generate PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default App; 